import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TbAuthService, StorageVars, OperationResult } from '@tb/auth';
import { CustomerService } from 'src/app/core/services/customer.service';
import { LoginRequest, LoginResponse, LoginService } from 'src/app/core/services/login.service';
import { WorkFlowService } from 'src/app/core/services/work-flow.service';
import { AdministrativeInfo } from 'src/app/model/AdministrativeInfo';
import { CASCompanyInfo } from 'src/app/model/CASCompanyInfo';
import { CodiceAziendaZucchetti, Customer, CustomerType, MandatiMago } from 'src/app/model/customer';
import { TbEnvironment } from 'src/app/model/environment';
import { ST_Roles, StoreRolesService } from './../../core/services/store-role.service';
import { MagoWebService } from 'src/app/core/services/mago-web.service';
import { AppComponent } from 'src/app/app/app.component';
import { NavigateService } from 'src/app/core/services/navigate.service';
import { RolesWeight } from 'src/app/authentication/auth-helpers';
import { LoadingService } from 'src/app/core/services/loading.service';
import { AccountService } from 'src/app/core/services/account.service';
import { ProductCode } from 'src/app/model/industry';
import { MiniStorageService } from 'src/app/core/services/mini-storage.service';
import { RoleState, StoreRole } from 'src/app/model/store-role';
import { ServiceUtilsService } from 'src/app/core/services/service-utils.service';
import { MiGenParameters } from 'src/app/model/MiGenParameters';
import { VerticalSolutionsService } from 'src/app/core/services/verticalsolutions.service';

interface CompanyInfoCheckResult {
    isValid: boolean;
    invalidProperties?: string; // In case of invalid properties.
}

@Component({
    selector: 'ms-cas-account',
    templateUrl: './cas-account.component.html',
    styleUrls: ['./cas-account.component.scss'],
})
export class CasAccountComponent implements OnInit {
    authorizationStored;
    authorizationProperties;
    ssid = '';
    companyInfo: CASCompanyInfo;
    loading = false;

    constructor(
        @Inject('env') private env: TbEnvironment,
        private route: ActivatedRoute,
        private flow: WorkFlowService,
        private loginService: LoginService,
        private customerService: CustomerService,
        private storeRoleService: StoreRolesService,
        private magoWebService: MagoWebService,
        private appComponent: AppComponent,
        private navigateService: NavigateService,
        private accountService: AccountService,
        private serviceUtilsService: ServiceUtilsService,
        private miniStorage: MiniStorageService,
        public authService: TbAuthService,
        public loadingService: LoadingService,
        public verticalSolutionService: VerticalSolutionsService
    ) {}

    ngOnInit(): void {
        this.route.queryParams.subscribe((params) => {
            let casUserId = params['CASUserId'];
            this.ssid = casUserId;
            this.loading = true;
            this.loadingService.setLoading(this.loading);

            let getCompanyInfo = this.loginService.getCASCompanyInfo(this.ssid).subscribe(
                (res) => {
                    this.companyInfo = res['Content'];

                    const companyInfoValidationResult: CompanyInfoCheckResult = this.validateCompanyInfo(this.companyInfo);

                    if (!companyInfoValidationResult.isValid) {
                        this.flow.showMessage(
                            'MyZ API Error',
                            `Could not get account informations from MyZ api: invalid properties (${companyInfoValidationResult.invalidProperties})`
                        );
                        this.loading = false;
                        this.loginService.logoutMYZ();
                        this.storeRoleService.Clear();
                        this.navigateService.goToHomePage();
                        return;
                    }
                    this.loginService.CASAccountInit(this.companyInfo.LOGINRETE, this.companyInfo.EMAIL, this.companyInfo.SSOID).subscribe(
                        (res) => {
                            const resData: any = res['InitResult'];
                            this.loginService.CASAccountLogin(res['GhostAccountName'], resData['JwtToken']).subscribe(
                                (r) => {
                                    this.loginService.setCASLoggedIn();
                                    resData.Email = this.companyInfo.EMAIL;
                                    resData.AccountName = res['GhostAccountName'];
                                    this.miniStorage.set('loginrete', this.companyInfo.LOGINRETE);
                                    this.miniStorage.set('codiceazienda', this.companyInfo.CODICEAZIENDA);
                                    this.checkProfilesBeforeRedirect(resData, this.companyInfo.CODICEAZIENDA, 'infinity');
                                    this.loading = false;
                                    this.verticalSolutionService.currentCodiceAzienda = this.companyInfo.CODICEAZIENDA;
                                },
                                (e) => {
                                    console.log(`Error 1: ${e}`);
                                    let loginResponse = e['error'];
                                    if (loginResponse.ResultCode === 19) {
                                        this.flow.showMessage('Login error', loginResponse.Message);
                                    }
                                    this.loading = false;
                                    this.navigateService.goToHomePage();
                                }
                            );
                        },
                        (err) => {
                            console.log(`Error 2: ${err}`);
                            let loginResponse = err['error'];
                            this.flow.showMessage('Login generic error', loginResponse.Message);
                            this.loading = false;
                            this.navigateService.goToHomePage();
                        }
                    );

                    getCompanyInfo.unsubscribe();
                },
                (err) => {
                    console.log(err);
                    this.loading = false;
                    getCompanyInfo.unsubscribe();
                }
            );
        });
    }

    updateSSOID(LOGINRETE: string, EMAIL: string, SSOID: string) {
        const accountName = this.companyInfo.LOGINRETE ? this.companyInfo.LOGINRETE : this.companyInfo.EMAIL;
        let account = {
            AccountName: accountName,
            Email: this.companyInfo.EMAIL,
            CASUser: SSOID,
            AuthenticationProvider: 'casonfly',
            FullName: `${this.companyInfo.NOME} ${this.companyInfo.COGNOME}`,
        };

        this.authorizationStored = this.loginService.getauthinfo();
        this.authorizationProperties = JSON.parse(this.authorizationStored);

        const accountOperation = this.accountService.saveAccount(account, this.authorizationProperties.accountName).subscribe(
            (accountResult) => {
                if (!accountResult.Result) {
                    this.flow.showMessage('UpdateSSOID failure', accountResult['message']);
                    accountOperation.unsubscribe();
                    return;
                }
                accountOperation.unsubscribe();
            },
            (err) => {
                this.flow.showMessage('UpdateSSOID failure', err['message']);
                accountOperation.unsubscribe();
            }
        );
    }

    validateCompanyInfo(info: CASCompanyInfo): CompanyInfoCheckResult {
        let incorrectProps = '';
        for (let prop in info) {
            if ((!info[prop] || info[prop] === 'undefined' || info[prop] === 'null') && prop !== 'LOGINRETE') {
                incorrectProps += `${prop} `;
            }
        }
        return {
            isValid: incorrectProps === '',
            invalidProperties: incorrectProps,
        };
    }
    async isDirettaLodi() {
        let opRes: OperationResult = await this.serviceUtilsService.getStoreParameterValue('direttaLodiEnabledAccounts');
        let content = new MiGenParameters();
        content = opRes.Content as MiGenParameters;
        let direttaLodiEnabledAccounts = content.Value.split(';');
        let isDirettaLodiAccountAllowed = direttaLodiEnabledAccounts.some(
            (x) => x.toLocaleLowerCase() == this.companyInfo.LOGINRETE.toLocaleLowerCase()
        );
        return isDirettaLodiAccountAllowed;
    }
    async checkProfilesBeforeRedirect(loginResponse: LoginResponse, codiceazienda: string, provider: string) {
        //devo capire se l'account che sta' entrando appartiene a più profili rivenditori/utenti
        //e in caso positivo redirezionarlo nella pagina della scelta del profilo con cui vuole essere autenticato
        //25/10/24 il codice azienda zucchetti 0000000000000001 non ha alcun mandato e devo aggiungerli io
        let magocloudCompanies: AdministrativeInfo[] = [];
        if (codiceazienda == CodiceAziendaZucchetti) {
            magocloudCompanies = this.customerService.setZuccchettiCompanyInfo(codiceazienda);

            let loginRequest = new LoginRequest();
            loginRequest.accountName = loginResponse.AccountName;
            loginRequest.password = '';
            loginRequest.appId = this.env.auth.appId;

            this.authorizationStored = this.loginService.getauthinfo();
            this.authorizationProperties = JSON.parse(this.authorizationStored);
            this.authService.errorMessage = '';
            this.GetRolesContent();
            await this.saveAccountName(loginRequest.accountName);

            //cerco i ruoli dello store
            await this.customerService.getCustomerMasterData(loginResponse.Email, loginResponse.AccountName, provider, true, codiceazienda);
            //
            await this.storeRoleService.setDefaultRoles(
                loginResponse.AccountName,
                this.customerService.companyInfo?.CompanyType,
                this.customerService.companyInfo?.Mandato
            );
            this.storeRoleService.setST_Roles(loginResponse.AccountName, ST_Roles[ST_Roles.ST_ZUCCHETTIACCOUNT].toString());
            await this.storeRoleService.saveSTRolesInSession(loginResponse.AccountName);

            if (this.authService.isSessionStorage()) {
                sessionStorage.setItem('magoCloudProfile_' + loginResponse.AccountName, magocloudCompanies[0]?.CompanyCode);
            } else {
                localStorage.setItem('magoCloudProfile_' + loginResponse.AccountName, magocloudCompanies[0]?.CompanyCode);
            }
            let selectedCompanyCode = magocloudCompanies[0]?.CompanyCode;
            this.magoWebService.currCompanyCode = selectedCompanyCode?.length > 0 ? selectedCompanyCode : '0110G081';
            this.magoWebService.currCompanyName = this.customerService.companyInfo?.CompanyName;
            this.verticalSolutionService.resellerCompanyName = this.customerService.companyInfo?.CompanyName;
            let isDirettaLodiAuthAccount = await this.isDirettaLodi();
            if (isDirettaLodiAuthAccount) {
                //aggiungo il ruolo
                let roleToAddORremove: StoreRole[] = [];
                let role = new StoreRole();
                role.Description = 'ST_ZUCCHETTIDIRETTACCOUNT';
                role.Disabled = 0;
                role.RoleName = 'ST_ZUCCHETTIDIRETTACCOUNT';
                role.State = RoleState.Added;
                roleToAddORremove.push(role);
                this.storeRoleService.setOrRemoveST_Roles(
                    this.authorizationProperties.accountName,
                    loginResponse.AccountName,
                    roleToAddORremove
                );
            }
            this.magoWebService.isZucchettiDiretta = isDirettaLodiAuthAccount;
            this.redirect();
        } else {
            let response: OperationResult = await this.customerService.getCustomerInfo(loginResponse.Email, provider, codiceazienda);

            if (response.Result) {
                let companies = response.Content as AdministrativeInfo[];
                let tmp: AdministrativeInfo[] = companies.filter(
                    (x) =>
                        x.CompanyType == CustomerType.Rivenditore &&
                        (x.ProductCode == ProductCode.MAGOCLD || x.ProductCode == ProductCode.MAGOWEB || x.ProductCode == ProductCode.MAGO4)
                );
                if (tmp.length > 0)
                    magocloudCompanies = tmp.filter((thing, i, arr) => arr.findIndex((t) => t.CompanyCode === thing.CompanyCode) === i);
                else magocloudCompanies.push(companies[0]); //è un utente finale

                this.customerService.setMagoCloudCompanies(magocloudCompanies);
            }

            let loginRequest = new LoginRequest();
            loginRequest.accountName = loginResponse.AccountName;
            loginRequest.password = '';
            loginRequest.appId = this.env.auth.appId;

            this.authorizationStored = this.loginService.getauthinfo();
            this.authorizationProperties = JSON.parse(this.authorizationStored);
            this.authService.errorMessage = '';
            this.GetRolesContent();
            await this.saveAccountName(loginRequest.accountName);
            //chiamo il microservizio che mi restituisce eventuali informazioni sull'indirizzo email che si è autenticato
            await this.customerService.getCustomerMasterData(
                loginResponse.Email,
                loginResponse.AccountName,
                provider,
                false,
                codiceazienda
            );
            await this.storeRoleService.setDefaultRoles(
                loginResponse.AccountName,
                this.customerService.companyInfo?.CompanyType,
                this.customerService.companyInfo?.Mandato
            );
            //aggiungo un ruolo che mi identifica l'account come account CAS
            this.storeRoleService.setST_Roles(loginResponse.AccountName, ST_Roles[ST_Roles.ST_CASACCOUNT].toString());
            // salvo il ruolo sul db dello store
            let roleToAddORremove: StoreRole[] = [];
            let role = new StoreRole();
            role.Description = 'ST_CASACCOUNT';
            role.Disabled = 0;
            role.RoleName = 'ST_CASACCOUNT';
            role.State = RoleState.Added;
            roleToAddORremove.push(role);
            this.storeRoleService.setOrRemoveST_Roles(
                this.authorizationProperties.accountName,
                loginResponse.AccountName,
                roleToAddORremove
            );
            //salvo in session i ruoli dello store
            await this.storeRoleService.saveSTRolesInSession(loginResponse.AccountName);

            if (this.authService.isSessionStorage()) {
                sessionStorage.setItem('magoCloudProfile_' + loginResponse.AccountName, magocloudCompanies[0]?.CompanyCode);
            } else {
                localStorage.setItem('magoCloudProfile_' + loginResponse.AccountName, magocloudCompanies[0]?.CompanyCode);
            }
            let selectedCompanyCode = magocloudCompanies[0]?.CompanyCode;
            this.magoWebService.currCompanyCode = selectedCompanyCode?.length > 0 ? selectedCompanyCode : '0110G081';
            this.magoWebService.currCompanyName = this.customerService.companyInfo?.CompanyName;
            this.magoWebService.isZucchettiDiretta = false;
            this.verticalSolutionService.resellerCompanyName = this.customerService.companyInfo?.CompanyName;
            this.redirect();
        }
    }

    GetRolesContent() {
        this.authorizationProperties.roles.forEach((x) => (x.RoleName = x.RoleName.split('-').join('')));
        this.authorizationProperties.roles.forEach((x) => (x.Weight = RolesWeight[x.RoleName]));
        this.authorizationStored = JSON.stringify(this.authorizationProperties);
        this.appComponent.ngOnInit();
        this.redirect();
    }

    redirect() {
        this.navigateService.goToDashboard();
    }

    private saveAccountName(accountName) {
        if (this.authService.isSessionStorage()) {
            sessionStorage.setItem(StorageVars.ACCOUNT_NAME, accountName);
        } else {
            localStorage.setItem(StorageVars.ACCOUNT_NAME, accountName);
        }
    }

    getCASUserId() {
        this.loginService.getCASUserId().subscribe({
            next: (r) => {
                console.log(r);
            },
            error: () => {
                console.log('error');
            },
        });
    }
}
